import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, AlertTriangle, Flag, Pin } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { PlanBoardEmployee, PlanSlotInfo } from './types';
import { BoardModel, DepartmentGroup, cellKey, headcountKey } from './boardModel';
import {
  DAY_OFF_CLASSES,
  dayMonth,
  employeeName,
  longDate,
  slotColorClass,
  slotHours,
  weekdayShort,
} from './planningUtils';

export interface DateMeta {
  date: string;
  isToday: boolean;
  isWeekend: boolean;
  holiday?: string;
  isExtraWorkDay: boolean;
}

// DOM ids used by the violations panel to scroll to a cell / row / headcount.
export const cellDomId = (employeeId: string, date: string) => `plan-cell-${employeeId}-${date}`;
export const rowDomId = (employeeId: string) => `plan-row-${employeeId}`;
export const positionDomId = (groupKey: string) => `plan-pos-${groupKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
export const headcountDomId = (positionId: string, slotId: string, date: string) =>
  `plan-hc-${positionId}-${slotId}-${date}`;

// Column backgrounds are informational only, so they stay neutral: red and
// amber on the board are reserved for errors and warnings.
// Leave markers are information, not a problem (a clash with a shift is
// reported as a violation), so they use a neutral stone tint.
export const ABSENCE_APPROVED_CLASSES =
  'bg-stone-200/80 text-stone-800 dark:bg-stone-700/70 dark:text-stone-100';
export const ABSENCE_PENDING_CLASSES =
  'border border-dashed border-stone-400 italic text-stone-700 dark:border-stone-500 dark:text-stone-300';

function columnTint(meta: DateMeta): string {
  if (meta.holiday && !meta.isExtraWorkDay) return 'bg-muted/70';
  if (meta.isWeekend) return 'bg-muted/40';
  return '';
}

/** Small filled badge marking a place covered by a confirmed exception. */
export const ExceptionMark: React.FC<{ className?: string; testId?: string }> = ({ className, testId }) => (
  <span
    data-testid={testId}
    aria-label="Wyjątek"
    role="img"
    className={cn(
      'inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm',
      className,
    )}
  >
    <Flag className="h-2.5 w-2.5" strokeWidth={2.75} />
  </span>
);

const ExceptionTipLines: React.FC<{ items?: Array<{ id: string; description: string }> }> = ({ items }) =>
  items && items.length > 0 ? (
    <>
      {items.map((c) => (
        <div key={c.id} className="flex items-start gap-1.5">
          <Flag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-300" />
          <span>Wyjątek: {c.description}</span>
        </div>
      ))}
    </>
  ) : null;

// ---------------------------------------------------------------------------
// Single floating tooltip for the whole board (one element instead of ~900
// Tooltip instances). Content is built on hover from the board indexes.
// ---------------------------------------------------------------------------

interface HoverTipHandle {
  show: (anchor: HTMLElement, content: React.ReactNode) => void;
  hide: () => void;
}

const HoverTip = forwardRef<HoverTipHandle>((_, ref) => {
  const [state, setState] = useState<{ x: number; y: number; below: boolean; content: React.ReactNode } | null>(
    null,
  );
  useImperativeHandle(ref, () => ({
    show: (anchor, content) => {
      const r = anchor.getBoundingClientRect();
      const below = r.top < 160;
      setState({ x: r.left + r.width / 2, y: below ? r.bottom + 6 : r.top - 6, below, content });
    },
    hide: () => setState(null),
  }));
  if (!state) return null;
  return createPortal(
    <div
      role="tooltip"
      className="fixed z-[60] max-w-xs px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg pointer-events-none"
      style={{
        left: Math.min(Math.max(state.x, 170), window.innerWidth - 170),
        top: state.y,
        transform: `translate(-50%, ${state.below ? '0' : '-100%'})`,
      }}
    >
      {state.content}
    </div>,
    document.body,
  );
});
HoverTip.displayName = 'HoverTip';

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

interface PlanRowProps {
  employee: PlanBoardEmployee;
  dates: DateMeta[];
  model: BoardModel;
  clickable: boolean;
}

const PlanRow = React.memo<PlanRowProps>(({ employee, dates, model, clickable }) => {
  const hasShift = employee.allowedSlotIds.length > 0;
  const rowViolations = model.violationsByEmployee.get(employee.employeeId);
  return (
    <tr id={rowDomId(employee.employeeId)} className="group">
      <th
        scope="row"
        data-tip="emp"
        data-emp={employee.employeeId}
        className="sticky left-0 z-10 bg-card group-hover:bg-muted border-b border-r px-3 py-1 text-left font-normal align-middle"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="font-medium truncate">{employeeName(employee)}</span>
          {rowViolations?.hard.length ? (
            <AlertCircle className="w-3.5 h-3.5 shrink-0 text-red-600 dark:text-red-400" aria-label="Błąd" />
          ) : rowViolations?.soft.length ? (
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-label="Ostrzeżenie" />
          ) : null}
        </div>
        <div className="text-xs truncate">
          {hasShift ? (
            <span className="text-muted-foreground">{employee.shiftName}</span>
          ) : (
            <span className="italic text-muted-foreground">Brak zmianowości</span>
          )}
        </div>
      </th>
      {dates.map((meta) => (
        <PlanCell
          key={meta.date}
          employee={employee}
          meta={meta}
          model={model}
          clickable={clickable}
          hasShift={hasShift}
        />
      ))}
    </tr>
  );
});
PlanRow.displayName = 'PlanRow';

interface PlanCellProps {
  employee: PlanBoardEmployee;
  meta: DateMeta;
  model: BoardModel;
  clickable: boolean;
  hasShift: boolean;
}

const PlanCell: React.FC<PlanCellProps> = ({ employee, meta, model, clickable, hasShift }) => {
  const key = cellKey(employee.employeeId, meta.date);
  const assignment = model.assignmentByCell.get(key);
  const absence = model.absenceByCell.get(key);
  const violations = model.violationsByCell.get(key);
  const exceptions = model.exceptionsByCell.get(key);
  const hard = (violations?.hard.length ?? 0) > 0;
  const soft = !hard && (violations?.soft.length ?? 0) > 0;

  let chip: React.ReactNode = null;
  if (assignment) {
    if (assignment.slotStart && assignment.slotEnd) {
      chip = (
        <span
          data-testid="plan-chip"
          className={cn(
            'rounded border px-1.5 py-0.5 text-xs font-semibold tabular-nums whitespace-nowrap',
            slotColorClass(assignment.slotStart),
            !assignment.shiftTimeId && 'line-through decoration-red-600',
          )}
        >
          {slotHours(assignment.slotStart, assignment.slotEnd)}
        </span>
      );
    } else {
      chip = (
        <span
          data-testid="plan-chip"
          className={cn('rounded border px-2 py-0.5 text-xs font-medium', DAY_OFF_CLASSES)}
        >
          W
        </span>
      );
    }
  }

  const inner = (
    <>
      {hard && (
        <AlertCircle className="absolute top-0.5 left-0.5 w-3.5 h-3.5 text-red-600 dark:text-red-400" aria-label="Błąd" />
      )}
      {soft && (
        <AlertTriangle
          className="absolute top-0.5 left-0.5 w-3.5 h-3.5 text-amber-600 dark:text-amber-400"
          aria-label="Ostrzeżenie"
        />
      )}
      {assignment?.isPinned && (
        <Pin className="absolute top-0.5 right-0.5 w-3 h-3 text-muted-foreground" aria-label="Przypięte" />
      )}
      {exceptions && (
        <ExceptionMark className="absolute bottom-0.5 right-0.5" testId="plan-exception-mark" />
      )}
      {chip}
      {absence && (
        <span
          className={cn(
            'max-w-full truncate rounded px-1 text-[10px] leading-4',
            exceptions && 'mr-3',
            absence.status === 'APPROVED' ? ABSENCE_APPROVED_CLASSES : ABSENCE_PENDING_CLASSES,
          )}
        >
          {absence.status === 'APPROVED' ? absence.label : `${absence.label}?`}
        </span>
      )}
    </>
  );

  const stateClasses = cn(
    'relative flex h-full min-h-[44px] w-full flex-col items-center justify-center gap-0.5 rounded px-0.5 py-1 transition-shadow',
    hard && 'ring-2 ring-inset ring-red-500 bg-red-50 dark:bg-red-950/40',
    soft && 'ring-2 ring-inset ring-amber-400 bg-amber-50 dark:bg-amber-950/30',
    exceptions && !hard && !soft && 'ring-1 ring-inset ring-primary/60',
    !hasShift &&
      'bg-[repeating-linear-gradient(45deg,transparent,transparent_9px,hsl(var(--border)/0.6)_9px,hsl(var(--border)/0.6)_10px)]',
  );

  return (
    <td className={cn('border-b border-r p-0.5 align-middle', columnTint(meta))}>
      {clickable ? (
        <button
          type="button"
          id={cellDomId(employee.employeeId, meta.date)}
          data-cell=""
          data-tip="cell"
          data-emp={employee.employeeId}
          data-date={meta.date}
          aria-label={`${employeeName(employee)}, ${longDate(meta.date)}`}
          className={cn(stateClasses, 'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary')}
        >
          {inner}
        </button>
      ) : (
        <div
          id={cellDomId(employee.employeeId, meta.date)}
          data-tip="cell"
          data-emp={employee.employeeId}
          data-date={meta.date}
          className={stateClasses}
        >
          {inner}
        </div>
      )}
    </td>
  );
};

// Met headcount is neutral; only shortfalls get a status colour.
export const HEADCOUNT_OK_CLASSES = 'font-medium text-muted-foreground';
export const HEADCOUNT_BELOW_TARGET_CLASSES =
  'font-semibold bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200';
export const HEADCOUNT_BELOW_MIN_CLASSES =
  'font-semibold bg-red-100 text-red-900 dark:bg-red-950/70 dark:text-red-200';

function headcountClasses(planned: number, min: number, target: number): string {
  if (planned < min) return HEADCOUNT_BELOW_MIN_CLASSES;
  if (planned < target) return HEADCOUNT_BELOW_TARGET_CLASSES;
  return HEADCOUNT_OK_CLASSES;
}

const HeadcountRow: React.FC<{
  positionId: string;
  slot: PlanSlotInfo;
  dates: DateMeta[];
  model: BoardModel;
}> = ({ positionId, slot, dates, model }) => {
  const byDate = model.headcount.get(positionId)?.get(slot.id);
  return (
    <tr>
      <th
        scope="row"
        className="sticky left-0 z-10 bg-muted border-b border-r px-3 py-1 text-left text-xs font-normal text-muted-foreground"
      >
        <span className="mr-1.5">Obsada</span>
        <span
          className={cn(
            'rounded border px-1 py-0.5 font-semibold tabular-nums',
            slotColorClass(slot.startTime),
          )}
        >
          {slotHours(slot.startTime, slot.endTime)}
        </span>
      </th>
      {dates.map((meta) => {
        const h = byDate?.get(meta.date);
        const hasException = model.exceptionsByHeadcount.has(headcountKey(positionId, slot.id, meta.date));
        return (
          <td key={meta.date} className="border-b border-r bg-muted/30 p-0.5 text-center text-xs">
            {h ? (
              <span
                id={headcountDomId(positionId, slot.id, meta.date)}
                data-tip="hc"
                data-pos={positionId}
                data-slot={slot.id}
                data-date={meta.date}
                data-testid="plan-headcount"
                className={cn(
                  'inline-flex min-w-[3rem] items-center justify-center gap-1 rounded px-1.5 py-0.5 tabular-nums',
                  headcountClasses(h.planned, h.minHeadcount, h.targetHeadcount),
                  hasException && 'ring-1 ring-inset ring-primary/60',
                )}
              >
                {h.planned}/{h.targetHeadcount}
                {hasException && <ExceptionMark testId="plan-exception-mark" />}
              </span>
            ) : (
              <span className="text-muted-foreground">–</span>
            )}
          </td>
        );
      })}
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Board
// ---------------------------------------------------------------------------

interface PlanBoardProps {
  groups: DepartmentGroup[];
  dates: DateMeta[];
  model: BoardModel;
  /** Cells are clickable (editor opens, editable or read-only). */
  clickable: boolean;
  onCellClick: (employeeId: string, date: string) => void;
}

export const PlanBoard: React.FC<PlanBoardProps> = ({ groups, dates, model, clickable, onCellClick }) => {
  const tipRef = useRef<HoverTipHandle>(null);
  const lastTipEl = useRef<HTMLElement | null>(null);

  const buildTip = useCallback(
    (el: HTMLElement): React.ReactNode | null => {
      const kind = el.dataset.tip;
      if (kind === 'emp') {
        const e = model.employeeById.get(el.dataset.emp || '');
        if (!e) return null;
        const rowV = model.violationsByEmployee.get(e.employeeId);
        return (
          <div className="space-y-1 whitespace-normal">
            <div className="font-semibold">{employeeName(e)}</div>
            <div>{e.positionName || 'Bez stanowiska'}</div>
            {e.allowedSlotIds.length > 0 ? (
              <div>Zmianowość: {e.shiftName}</div>
            ) : (
              <div>
                Brak zmianowości — nie można go planować. Przypisz zmianowość pracownikowi albo ustaw
                domyślną zmianowość jego stanowiska.
              </div>
            )}
            {rowV && [...rowV.hard, ...rowV.soft].map((v, i) => <div key={i}>• {v.message}</div>)}
          </div>
        );
      }
      if (kind === 'cell') {
        const empId = el.dataset.emp || '';
        const date = el.dataset.date || '';
        const e = model.employeeById.get(empId);
        if (!e) return null;
        const key = cellKey(empId, date);
        const a = model.assignmentByCell.get(key);
        const abs = model.absenceByCell.get(key);
        const v = model.violationsByCell.get(key);
        const slot = a?.shiftTimeId ? model.slotById.get(a.shiftTimeId) : undefined;
        const holiday = model.holidayByDate.get(date);
        return (
          <div className="space-y-1 whitespace-normal">
            <div className="font-semibold">{employeeName(e)}</div>
            <div>{longDate(date)}</div>
            {holiday && <div>Święto: {holiday}</div>}
            <ExceptionTipLines items={model.exceptionsByCell.get(key)} />
            {a ? (
              a.slotStart ? (
                <div>
                  Zmiana {slotHours(a.slotStart, a.slotEnd)}
                  {slot ? ` (${slot.shiftName})` : ' — tej zmiany już nie ma w zmianowości'}
                </div>
              ) : (
                <div>Dzień wolny</div>
              )
            ) : (
              <div>Nie zaplanowano</div>
            )}
            {a && (
              <div>
                {a.source === 'ALGORITHM' ? 'Ułożone automatycznie' : a.source === 'IMPORT' ? 'Z importu z Excela' : 'Ustawione ręcznie'}
                {a.isPinned ? ', przypięte' : ''}
              </div>
            )}
            {a?.note && <div>Notatka: {a.note}</div>}
            {abs && (
              <div>
                {abs.status === 'APPROVED' ? 'Nieobecność (zatwierdzona)' : 'Wniosek oczekuje'}: {abs.label}
              </div>
            )}
            {v && [...v.hard, ...v.soft].map((x, i) => <div key={i}>• {x.message}</div>)}
          </div>
        );
      }
      if (kind === 'hc') {
        const pos = el.dataset.pos || '';
        const slotId = el.dataset.slot || '';
        const date = el.dataset.date || '';
        const h = model.headcount.get(pos)?.get(slotId)?.get(date);
        if (!h) return null;
        const status =
          h.planned < h.minHeadcount
            ? 'Poniżej minimum'
            : h.planned < h.targetHeadcount
            ? 'Poniżej docelowej obsady'
            : 'Obsada wystarczająca';
        return (
          <div className="space-y-1 whitespace-normal">
            <div className="font-semibold">{status}</div>
            <div>Zaplanowano: {h.planned}</div>
            <div>Minimum: {h.minHeadcount}</div>
            <div>Docelowo: {h.targetHeadcount}</div>
            <ExceptionTipLines items={model.exceptionsByHeadcount.get(headcountKey(pos, slotId, date))} />
          </div>
        );
      }
      return null;
    },
    [model],
  );

  const handleMouseOver = (e: React.MouseEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-tip]');
    if (el === lastTipEl.current) return;
    lastTipEl.current = el;
    if (!el) {
      tipRef.current?.hide();
      return;
    }
    const content = buildTip(el);
    if (content) tipRef.current?.show(el, content);
    else tipRef.current?.hide();
  };

  const handleMouseLeave = () => {
    lastTipEl.current = null;
    tipRef.current?.hide();
  };

  const handleClick = (e: React.MouseEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-cell]');
    if (!el) return;
    tipRef.current?.hide();
    lastTipEl.current = null;
    onCellClick(el.dataset.emp || '', el.dataset.date || '');
  };

  return (
    <>
      <div
        className="relative max-h-[calc(100vh-7rem)] scroll-mt-24 overflow-auto rounded-lg border bg-card"
        data-testid="plan-board"
        onScroll={handleMouseLeave}
      >
        <table
          className="w-full border-separate border-spacing-0 text-sm"
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        >
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 min-w-[190px] w-[220px] bg-card border-b border-r px-3 py-2 text-left font-semibold">
                Pracownik
              </th>
              {dates.map((meta) => (
                <th
                  key={meta.date}
                  data-testid={`plan-day-${meta.date}`}
                  className={cn(
                    'sticky top-0 z-20 min-w-[96px] border-b border-r bg-card px-1 py-1.5 text-center font-semibold',
                    meta.isToday && 'shadow-[inset_0_-2px_0_hsl(var(--primary))]',
                  )}
                >
                  <div className={cn(meta.isToday && 'text-primary')}>
                    {weekdayShort(meta.date)} {dayMonth(meta.date)}
                  </div>
                  {meta.isToday && <div className="text-[10px] font-medium text-primary">dziś</div>}
                  {meta.holiday && (
                    <div
                      className="mx-auto max-w-[92px] truncate text-[10px] font-medium text-muted-foreground"
                      title={meta.holiday}
                    >
                      {meta.holiday}
                      {meta.isExtraWorkDay ? ' (pracujący)' : ''}
                    </div>
                  )}
                  {!meta.holiday && meta.isExtraWorkDay && (
                    <div className="text-[10px] font-medium text-muted-foreground">dzień pracujący</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((dept) => (
              <React.Fragment key={dept.key}>
                <tr>
                  <th
                    colSpan={1}
                    className="sticky left-0 z-10 bg-secondary border-b border-r px-3 py-1.5 text-left text-sm font-bold text-secondary-foreground"
                  >
                    {dept.departmentName}
                  </th>
                  <td colSpan={7} className="bg-secondary border-b" />
                </tr>
                {dept.positions.map((pos) => (
                  <React.Fragment key={pos.key}>
                    <tr id={positionDomId(pos.key)}>
                      <th className="sticky left-0 z-10 bg-muted border-b border-r px-3 py-1 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {pos.positionName}{' '}
                        <span className="font-normal normal-case">({pos.employees.length})</span>
                      </th>
                      <td colSpan={7} className="bg-muted border-b" />
                    </tr>
                    {pos.employees.map((emp) => (
                      <PlanRow
                        key={emp.employeeId}
                        employee={emp}
                        dates={dates}
                        model={model}
                        clickable={clickable}
                      />
                    ))}
                    {pos.positionId &&
                      pos.headcountSlots.map((slot) => (
                        <HeadcountRow
                          key={slot.id}
                          positionId={pos.positionId as string}
                          slot={slot}
                          dates={dates}
                          model={model}
                        />
                      ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <HoverTip ref={tipRef} />
    </>
  );
};

